import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { LEGAL_DOCUMENT_LABELS, LegalDocument, LegalSlug } from 'types/legal';
import { PUBLIC_LEGAL_PATHS } from 'utils/legal';
import LegalDocumentView from './LegalDocumentView';
import { documentMetaLine, errorMessage } from './legalUtils';

interface LegalDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  slug: LegalSlug;
  // Either a document already in hand, or a loader that runs each time the dialog opens.
  document?: LegalDocument | null;
  load?: () => Promise<LegalDocument>;
  // Off for admin views of a specific version, where the public page (always the
  // current document) would show something else.
  showOpenInNewTab?: boolean;
}

const labelFor = (slug: LegalSlug) => (slug === 'terms' ? LEGAL_DOCUMENT_LABELS.TERMS : LEGAL_DOCUMENT_LABELS.PRIVACY_POLICY);

const LegalDocumentDialog: React.FC<LegalDocumentDialogProps> = ({ open, onClose, slug, document, load, showOpenInNewTab = true }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [loaded, setLoaded] = useState<LegalDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The loader is usually an inline closure; keep it out of the effect's dependencies.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!open || document || !loadRef.current) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setLoaded(null);
    loadRef
      .current()
      .then((doc) => {
        if (!cancelled) setLoaded(doc);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(
          err?.statusCode === 404 ? 'This document has not been published yet.' : errorMessage(err, 'Could not load this document.')
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, slug, document]);

  const doc = document ?? loaded;
  const title = doc?.title || labelFor(slug);
  const titleId = `legal-dialog-title-${slug}`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={fullScreen} scroll="paper" aria-labelledby={titleId}>
      <DialogTitle id={titleId} sx={{ pr: 7 }} data-no-translate="true">
        <Typography variant="h4" component="span" sx={{ display: 'block' }}>
          {title}
        </Typography>
        {doc && (
          <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block', mt: 0.5 }}>
            {documentMetaLine(doc)}
          </Typography>
        )}
        <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 240 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress aria-label="Loading document" />
          </Box>
        )}
        {!loading && error && <Alert severity="info">{error}</Alert>}
        {!loading && !error && doc && <LegalDocumentView content={doc.content} hideHeader />}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Stack direction="row" spacing={1} sx={{ width: '100%', justifyContent: showOpenInNewTab ? 'space-between' : 'flex-end' }}>
          {showOpenInNewTab && (
            <Button
              component="a"
              href={PUBLIC_LEGAL_PATHS[slug]}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<OpenInNewIcon />}
              variant="outlined"
              color="secondary"
            >
              Open in new tab
            </Button>
          )}
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default LegalDocumentDialog;
